import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import getDiscountUrgencyPromoStats, {
  getTripsForDiscountStats,
} from 'utils/urgencyDiscountBanner';
import { getMomentFromSearchDateParam } from 'utils/search/searchDateParam';
import { trackUrgencyBannerShown } from 'metrics/user-analytics/search';
import useUrgencyDiscountUIEnabled from 'hooks/useUrgencyDiscountUIEnabled';
import useSearch from 'hooks/store/useSearch';
import { shouldShowProviderDiscountForSearch } from 'utils/passengerCategories';
import useGrowthBookFeatureValue from 'components/GrowthBookProvider/useGrowthBookFeatureValue';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import PromoUrgencyBanner from 'components/search/PromoUrgencyBanner';

const trackedUrgencyBannerImpressions = new Set();

/**
 * Displays and tracks the urgency discount banner for a result list.
 *
 * @param {object} props - Component props.
 * @param {Array} props.trips - Trips represented by the result list.
 * @param {boolean} props.roundTrip - Whether the search is round trip.
 * @param {string} props.way - Current trip direction.
 * @param {object} props.analyticsSearchData - Normalized search analytics data.
 * @param {boolean} props.searchIsPolling - Whether the search is still polling.
 * @param {string} props.departureDate - Date currently shown in departure results.
 * @param {object|null} props.departureTrip - Selected departure trip for return validation.
 * @returns {JSX.Element|null} Urgency banner when its visibility requirements are met.
 */
const PromoUrgencyBannerContainer = ({
  trips,
  roundTrip,
  way,
  analyticsSearchData,
  searchIsPolling,
  departureDate,
  departureTrip,
}) => {
  const urgencyDiscountUIEnabled = useUrgencyDiscountUIEnabled();
  const { passengers: searchPassengers } = useSearch();
  const categoryBasedPricing = useGrowthBookFeatureValue('category_based_pricing');
  const halfPriceCategoryFallback = useGrowthBookFeatureValue('half_price_category_fallback');
  const features = useWhitelabelFeatures() || {};

  /**
   * Checks whether a trip can display its provider discount for the current search.
   *
   * @param {object} trip - Trip result.
   * @returns {boolean} Whether its provider discount is visible.
   */
  const isProviderDiscountVisible = (trip) =>
    Boolean(trip) &&
    shouldShowProviderDiscountForSearch(
      searchPassengers,
      Array.isArray(trip.passengerTypes) ? trip.passengerTypes : [],
      Boolean(categoryBasedPricing),
      Boolean(halfPriceCategoryFallback),
    );
  const tripsWithVisibleProviderDiscount = trips.filter(isProviderDiscountVisible);
  const tripsForDiscountStats = getTripsForDiscountStats({
    trips: tripsWithVisibleProviderDiscount,
    way,
    validateRoundTripDiscount: Boolean(features.VALIDATE_ROUND_TRIP_DISCOUNT),
    departureTrip,
  });
  const promoUrgencyStats = getDiscountUrgencyPromoStats({
    trips: tripsForDiscountStats,
    roundTrip,
    way,
  });
  const currentSearchDate = departureDate
    ? getMomentFromSearchDateParam(departureDate).startOf('day').format()
    : null;
  const shouldShowBanner =
    urgencyDiscountUIEnabled &&
    promoUrgencyStats.scheduleCount > 0 &&
    promoUrgencyStats.discountPercent > 0;
  useEffect(() => {
    if (!shouldShowBanner || searchIsPolling || !analyticsSearchData) return;

    const impressionKey = [
      analyticsSearchData.id,
      promoUrgencyStats.currentRoute,
      currentSearchDate,
    ].join('|');

    if (trackedUrgencyBannerImpressions.has(impressionKey)) return;

    trackedUrgencyBannerImpressions.add(impressionKey);
    trackUrgencyBannerShown(analyticsSearchData, {
      ...promoUrgencyStats,
      currentDate: currentSearchDate,
    });
  }, [
    analyticsSearchData,
    currentSearchDate,
    promoUrgencyStats,
    searchIsPolling,
    shouldShowBanner,
  ]);

  if (!shouldShowBanner) return null;

  return (
    <PromoUrgencyBanner
      scheduleCount={promoUrgencyStats.scheduleCount}
      discountPercent={promoUrgencyStats.discountPercent}
      ticketsSold={promoUrgencyStats.ticketsSold}
    />
  );
};

PromoUrgencyBannerContainer.propTypes = {
  trips: PropTypes.array.isRequired,
  roundTrip: PropTypes.bool,
  way: PropTypes.string.isRequired,
  analyticsSearchData: PropTypes.object,
  searchIsPolling: PropTypes.bool,
  departureDate: PropTypes.string,
  departureTrip: PropTypes.object,
};

PromoUrgencyBannerContainer.defaultProps = {
  roundTrip: false,
  analyticsSearchData: null,
  searchIsPolling: false,
  departureDate: null,
  departureTrip: null,
};

export default PromoUrgencyBannerContainer;
